import { gql } from '@apollo/client';
import { inflect } from 'inflection';
import _ from 'lodash';
import React, { useMemo } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import BorderedIcon from '../../components/BorderedIcon';
import CallOut from '../../components/CallOut';
import Tooltip from '../../components/Tooltip';
import type { SharingDropdownAccessList_PostFragment } from '../../graphql/generated/graphqlTypes';
import {
  PostLinkAccess,
  PostShareRole,
} from '../../graphql/generated/graphqlTypes';
import GroupIcon from '../../groups/GroupIcon';
import { useAllGroupMap } from '../../groups/useAllGroups';
import linkIcon from '../../icons/link.svg';
import publicIcon from '../../icons/public.svg';
import TeamIcon from '../../organization/TeamIcon';
import TopicIcon from '../../topic/TopicIcon';
import TopicNameWithIcon from '../../topic/TopicNameWithIcon';
import topicPolicyCheck, {
  TopicPolicyAction,
  TopicRole,
  UserRole,
} from '../../topic/topicPolicyCheck';
import { useAllTopicMap } from '../../topic/useAllTopics';
import getTopicUsers from '../../topic/utils/getTopicUsers';
import { useAllUsersMap } from '../../user/useAllUsers';
import UserRow from '../../user/UserRow';
import { nonEmptyId } from '../../utils/nonEmpty';
import populateObjects from '../../utils/populateObjects';
import {
  isAllowedThroughLinkAccess,
  isAllowedThroughTopicSharing,
  PostPolicyAction,
  usePostPolicyCheck,
} from '../postPolicyCheck';
import getRemoveTopicDisabledMessage from './getRemoveTopicDisabledMessage';
import {
  useRemoveGroupFromPost,
  useRemovePostUser,
  useRemoveTopicFromPost,
  useUpdatePostGroup,
  useUpdatePostLinkAccess,
  useUpdatePostUser,
} from './postPermissionOperations';
import SharingDropdownAccessLevelSelect from './SharingDropdownAccessLevelSelect';
import styles from './SharingDropdownAccessList.module.scss';
import SharingDropdownRow from './SharingDropdownRow';

const SHARING_DROPDOWN_POST_FRAGMENT = gql`
  fragment SharingDropdownAccessList_post on Post {
    id
    linkAccess
    publishedAt
    owner {
      ...UserRow_user
    }
    topics {
      id
    }
    sharing {
      editorIds
      viewerIds
      editorGroupIds
      viewerGroupIds
    }
    ...usePostPolicyCheck_post
  }
  ${UserRow.fragments.user}
  ${usePostPolicyCheck.fragments.post}
`;

const TOPIC_ROLE_DISABLE_MESSAGE =
  "Access granted by topic's settings. Please change the topic's settings to modify edit/view access.";
const LINK_ROLE_TOOLTIP = 'Access set by topics this post is organized in';

interface SharingDropdownProps {
  post: SharingDropdownAccessList_PostFragment;
}

const SharingDropdownAccessList = ({
  post,
  post: { publishedAt, owner, sharing },
}: SharingDropdownProps) => {
  const { editorIds, viewerIds, editorGroupIds, viewerGroupIds } =
    sharing || {};

  const allTopicMap = useAllTopicMap();
  const teamName = useAppSelector((state) => state.session.organization.name);
  const currentUser = useAppSelector((state) => state.session.user);
  const postPolicyCheck = usePostPolicyCheck();

  const postWithFullTopics = useMemo(
    () => ({
      ...post,
      topics: _.compact(post.topics.map(({ id }) => allTopicMap[id])),
    }),
    [post, allTopicMap],
  );

  const canEditUsersAndLink = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    currentUser,
  );

  const removeTopicMutation = useRemoveTopicFromPost();
  const removeTopic = _.partial(removeTopicMutation, post);

  const updateLinkAccess = useUpdatePostLinkAccess();
  const removeUser = useRemovePostUser();
  const updateUser = useUpdatePostUser();

  const removeGroup = useRemoveGroupFromPost();
  const updateGroup = useUpdatePostGroup();

  const isPublicThroughLink = isAllowedThroughLinkAccess(
    PostPolicyAction.ViewPost,
    post,
    UserRole.Anonymous,
  );
  const isPublicThroughTopics = isAllowedThroughTopicSharing(
    PostPolicyAction.ViewPost,
    postWithFullTopics,
    UserRole.Anonymous,
  );
  const isPublic = isPublicThroughLink || isPublicThroughTopics;
  const canFullUserView = postPolicyCheck(
    PostPolicyAction.ViewPost,
    post,
    UserRole.Full,
  );
  const canFullUserEdit = postPolicyCheck(
    PostPolicyAction.EditPost,
    post,
    UserRole.Full,
  );

  const topics = useMemo(
    () =>
      postWithFullTopics.topics.map((topic) => {
        const canMembersEdit =
          canFullUserEdit ||
          topicPolicyCheck(TopicPolicyAction.EditPost, topic, TopicRole.Member);

        const { members, owners } = getTopicUsers(topic);
        const memberCount = members.length;
        const ownerCount = owners.length;
        const ownerString = ownerCount
          ? `${ownerCount} ${inflect('owner', ownerCount)}`
          : null;
        const memberString = memberCount
          ? `${memberCount} ${inflect('member', memberCount)}`
          : null;

        const combinedString = _.compact([ownerString, memberString]).join(
          ' and ',
        );

        return {
          canMembersEdit,
          memberString,
          ownerString,
          combinedString,
          topic,
        };
      }),
    [canFullUserEdit, postWithFullTopics.topics],
  );

  const allUsersMap = useAllUsersMap();
  const editorUsers = useMemo(
    () => populateObjects(allUsersMap, editorIds).filter(nonEmptyId),
    [allUsersMap, editorIds],
  );
  const viewerUsers = useMemo(
    () => populateObjects(allUsersMap, viewerIds).filter(nonEmptyId),
    [allUsersMap, viewerIds],
  );

  const allGroupsMap = useAllGroupMap();
  const editorGroups = useMemo(
    () => populateObjects(allGroupsMap, editorGroupIds).filter(nonEmptyId),
    [allGroupsMap, editorGroupIds],
  );
  const viewerGroups = useMemo(
    () => populateObjects(allGroupsMap, viewerGroupIds).filter(nonEmptyId),
    [allGroupsMap, viewerGroupIds],
  );

  return (
    <div>
      {!publishedAt && topics.length > 0 && (
        <div className={styles.row}>
          <CallOut variant="warning">
            Topic access will take effect after publishing
          </CallOut>
        </div>
      )}
      {owner && (
        <SharingDropdownRow
          info={<UserRow showBadge user={owner} />}
          right="Owner"
        />
      )}
      {editorUsers.map((user) => (
        <SharingDropdownRow
          key={user.id}
          info={<UserRow user={user} showBadge />}
          right={
            canEditUsersAndLink ? (
              <SharingDropdownAccessLevelSelect
                value="EDITOR"
                onRemove={() => removeUser(post, user.id)}
                onChange={(value) =>
                  updateUser(
                    post,
                    user.id,
                    value === 'EDITOR'
                      ? PostShareRole.Editor
                      : PostShareRole.Viewer,
                  )
                }
              />
            ) : (
              'Editor'
            )
          }
        />
      ))}
      {viewerUsers.map((user) => (
        <SharingDropdownRow
          key={user.id}
          info={<UserRow user={user} showBadge />}
          right={
            canEditUsersAndLink ? (
              <SharingDropdownAccessLevelSelect
                value="VIEWER"
                onRemove={() => removeUser(post, user.id)}
                onChange={(value) =>
                  updateUser(
                    post,
                    user.id,
                    value === 'EDITOR'
                      ? PostShareRole.Editor
                      : PostShareRole.Viewer,
                  )
                }
              />
            ) : (
              'Viewer'
            )
          }
        />
      ))}
      {editorGroups.map((group) => (
        <SharingDropdownRow
          key={group.id}
          icon={<GroupIcon />}
          title={group.name}
          description={`${group.allUsers.length} members`}
          right={
            canEditUsersAndLink ? (
              <SharingDropdownAccessLevelSelect
                value="EDITOR"
                onRemove={() => removeGroup(post, group)}
                onChange={(value) =>
                  updateGroup(
                    post,
                    group,
                    value === 'EDITOR'
                      ? PostShareRole.Editor
                      : PostShareRole.Viewer,
                  )
                }
              />
            ) : (
              'Editor'
            )
          }
        />
      ))}
      {viewerGroups.map((group) => (
        <SharingDropdownRow
          key={group.id}
          icon={<GroupIcon />}
          title={group.name}
          description={`${group.allUsers.length} members`}
          right={
            canEditUsersAndLink ? (
              <SharingDropdownAccessLevelSelect
                value="VIEWER"
                onRemove={() => removeGroup(post, group)}
                onChange={(value) =>
                  updateGroup(
                    post,
                    group,
                    value === 'EDITOR'
                      ? PostShareRole.Editor
                      : PostShareRole.Viewer,
                  )
                }
              />
            ) : (
              'Viewer'
            )
          }
        />
      ))}
      {topics.map(({ topic, canMembersEdit, ownerString, combinedString }) => {
        const userString = canMembersEdit ? combinedString : ownerString;

        return (
          !!userString && (
            <SharingDropdownRow
              key={topic.id}
              icon={<TopicIcon />}
              title={<TopicNameWithIcon topic={topic} />}
              description={userString}
              right={
                <SharingDropdownAccessLevelSelect
                  changeDisabledMessage={TOPIC_ROLE_DISABLE_MESSAGE}
                  removeDisabledMessage={getRemoveTopicDisabledMessage(
                    topic,
                    currentUser,
                  )}
                  value="EDITOR"
                  onRemove={() => removeTopic(topic)}
                />
              }
            />
          )
        );
      })}
      {topics
        .filter((t) => !t.canMembersEdit && t.memberString)
        .map(({ topic, memberString }) => (
          <SharingDropdownRow
            key={topic.id}
            icon={<TopicIcon />}
            title={<TopicNameWithIcon topic={topic} />}
            description={memberString}
            right={
              <SharingDropdownAccessLevelSelect
                changeDisabledMessage={TOPIC_ROLE_DISABLE_MESSAGE}
                removeDisabledMessage={getRemoveTopicDisabledMessage(
                  topic,
                  currentUser,
                )}
                value="VIEWER"
                onRemove={() => removeTopic(topic)}
              />
            }
          />
        ))}
      {isPublic && (
        <SharingDropdownRow
          icon={<BorderedIcon>{publicIcon}</BorderedIcon>}
          title="Public"
          description="Everyone on the internet can access"
          right={
            isPublicThroughTopics || !canEditUsersAndLink ? (
              <Tooltip
                tooltip={LINK_ROLE_TOOLTIP}
                trigger="hover"
                placement="bottom"
              >
                Viewer
              </Tooltip>
            ) : (
              <SharingDropdownAccessLevelSelect
                value="VIEWER"
                options={['VIEWER']}
                onRemove={() => updateLinkAccess(post, PostLinkAccess.Internal)}
              />
            )
          }
        />
      )}
      {!isPublic &&
        canFullUserView &&
        (publishedAt ? (
          <SharingDropdownRow
            icon={<TeamIcon size="medium" />}
            title={teamName}
            description="Any standard user can access"
            right={
              <Tooltip
                tooltip={LINK_ROLE_TOOLTIP}
                trigger="hover"
                placement="bottom"
              >
                {canFullUserEdit ? 'Editor' : 'Viewer'}
              </Tooltip>
            }
          />
        ) : (
          <SharingDropdownRow
            icon={<BorderedIcon>{linkIcon}</BorderedIcon>}
            title="Direct"
            description="Teammates with this link can edit"
            right={
              canEditUsersAndLink ? (
                <SharingDropdownAccessLevelSelect
                  changeDisabledMessage={
                    <>
                      Editor access granted by &ldquo;Link access&rdquo;
                      settings below. Change to &ldquo;Disabled&rdquo; to
                      specify access for individual users.
                    </>
                  }
                  value="EDITOR"
                  onRemove={() =>
                    updateLinkAccess(post, PostLinkAccess.Disabled)
                  }
                />
              ) : (
                'Editor'
              )
            }
          />
        ))}
    </div>
  );
};

SharingDropdownAccessList.fragments = {
  post: SHARING_DROPDOWN_POST_FRAGMENT,
};

export default SharingDropdownAccessList;
